/**
 * Opportunity Maps Module
 * 
 * Allows experts to create and manage Opportunity Maps - the key component
 * of the Perfect Brain that defines pathways with requirements, blockers,
 * unlock factors, and alternatives for intelligent opportunity discovery.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

// ============================================
// SVG ICONS
// ============================================

const Icons = {
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  loader: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  map: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  pathway: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  block: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
  unlock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>,
  alt: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>,
  chevronDown: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  chevronUp: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>,
};

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  padding: 24px;
  animation: ${fadeIn} 0.3s ease-out;
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const TitleSection = styled.div``;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  color: rgba(248, 250, 252, 0.6);
  font-size: 14px;
  margin: 4px 0 0 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.$primary && css`
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    border: none;
    color: white;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4); }
  `}
  
  ${props => props.$secondary && css`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #f8fafc;
    &:hover { background: rgba(255, 255, 255, 0.1); }
  `}
  
  ${props => props.$danger && css`
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    &:hover { background: rgba(239, 68, 68, 0.2); }
  `}
  
  ${props => props.$success && css`
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #10b981;
    &:hover { background: rgba(16, 185, 129, 0.2); }
  `}
  
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const LoadingSpinner = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(248, 250, 252, 0.6);
  
  svg { width: 48px; height: 48px; margin-bottom: 16px; opacity: 0.5; }
  h3 { color: #f8fafc; margin-bottom: 8px; }
  p { margin-bottom: 24px; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #6366f1, #4f46e5)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active 
    ? 'transparent' 
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? 'white' : 'rgba(248, 250, 252, 0.7)'};
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #6366f1, #4f46e5)' 
      : 'rgba(255, 255, 255, 0.1)'};
    color: white;
  }
`;

const FilterCount = styled.span`
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
`;

const MapsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 20px;
`;

const MapCard = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9));
  border: 1px solid ${props => props.$active ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
`;

const MapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const MapTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0;
`;

const MapStatus = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  
  ${props => props.$status === 'active' && css`
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  `}
  ${props => props.$status === 'draft' && css`
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
  `}
  ${props => props.$status === 'archived' && css`
    background: rgba(107, 114, 128, 0.15);
    color: #6b7280;
  `}
`;

const MapStats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const MapStat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(248, 250, 252, 0.7);
  
  svg { opacity: 0.6; }
`;

const MapActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const IconButton = styled.button`
  padding: 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(248, 250, 252, 0.7);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #f8fafc;
  }
  
  ${props => props.$danger && css`
    &:hover { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
  `}
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: linear-gradient(180deg, rgba(30, 35, 55, 0.98) 0%, rgba(20, 25, 40, 0.99) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: 18px;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: rgba(248, 250, 252, 0.8);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #f8fafc;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder { color: rgba(248, 250, 252, 0.4); }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #f8fafc;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #f8fafc;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  option { background: #1e293b; }
`;

// Pathway Section Styles
const PathwaySection = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 16px;
`;

const PathwayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  
  &:hover { background: rgba(255, 255, 255, 0.02); }
`;

const PathwayTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  color: #f8fafc;
`;

const PathwayBody = styled.div`
  padding: 0 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const SubSection = styled.div`
  margin-top: 16px;
`;

const SubSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.$color || 'rgba(248, 250, 252, 0.5)'};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ItemTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.$bg || 'rgba(99, 102, 241, 0.15)'};
  border: 1px solid ${props => props.$border || 'rgba(99, 102, 241, 0.3)'};
  border-radius: 6px;
  font-size: 12px;
  color: ${props => props.$color || '#a5b4fc'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$bgHover || 'rgba(99, 102, 241, 0.25)'};
  }
`;

const ItemRemoveBtn = styled.button`
  background: none;
  border: none;
  color: rgba(248, 250, 252, 0.5);
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #ef4444;
  }
`;

const InlineEditWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  width: 100%;
`;

const InlineEditRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const InlineInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #f8fafc;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder { color: rgba(248, 250, 252, 0.4); }
`;

const InlineTextArea = styled.textarea`
  flex: 1;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #f8fafc;
  font-size: 13px;
  min-height: 60px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder { color: rgba(248, 250, 252, 0.4); }
`;

const InlineButton = styled.button`
  padding: 6px 12px;
  background: ${props => props.$primary ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$primary ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  color: ${props => props.$primary ? '#a5b4fc' : 'rgba(248, 250, 252, 0.7)'};
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.$primary ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const AddItemButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: rgba(248, 250, 252, 0.5);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    color: #a5b4fc;
  }
`;

// ============================================
// MAIN COMPONENT
// ============================================

const OpportunityMapsModule = () => {
  const axios = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maps, setMaps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [expandedPathways, setExpandedPathways] = useState({});
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, draft, archived
  
  // Inline editing state - tracks which item is being edited
  // Format: { pathwayId, itemType: 'requirement'|'blocker'|'unlockFactor'|'alternative', itemId, isNew: boolean }
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemData, setEditingItemData] = useState({ name: '', description: '', discoveryQuestion: '' });
  
  // Seeding state
  const [seeding, setSeeding] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    domain: '',
    domainDisplayName: '',
    status: 'draft',
    pathways: [],
    globalUnlockFactors: [],
    universalDiscoveryQuestions: []
  });

  // Fetch opportunity maps
  const fetchMaps = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/brain/v2/opportunity-maps');
      if (response.data?.data) {
        setMaps(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching opportunity maps:', error);
      showToast('Failed to load opportunity maps', 'error');
    } finally {
      setLoading(false);
    }
  }, [axios, showToast]);

  // Fetch knowledge categories for linking
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get('/api/context-manager/categories');
      if (response.data?.categories) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [axios]);

  useEffect(() => {
    fetchMaps();
    fetchCategories();
  }, [fetchMaps, fetchCategories]);

  // Seed default immigration templates
  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await axios.post('/api/brain/v2/opportunity-maps/seed-defaults');
      if (response.data?.success) {
        showToast(`Created ${response.data.created} opportunity maps. Total: ${response.data.total}`, 'success');
        fetchMaps();
      }
    } catch (error) {
      console.error('Error seeding defaults:', error);
      showToast('Failed to seed default maps', 'error');
    } finally {
      setSeeding(false);
    }
  };

  // Open modal for new map
  const handleCreateMap = () => {
    setEditingMap(null);
    setFormData({
      domain: '',
      domainDisplayName: '',
      status: 'draft',
      pathways: [],
      globalUnlockFactors: [],
      universalDiscoveryQuestions: []
    });
    setExpandedPathways({});
    setShowModal(true);
  };

  // Create example Work Permits map
  const handleCreateExample = () => {
    setEditingMap(null);
    const exampleData = {
      domain: 'work-permits',
      domainDisplayName: 'Work Permit Pathways',
      status: 'draft',
      pathways: [
        {
          id: 'pathway_lmia',
          name: 'lmia-work-permit',
          displayName: 'LMIA Work Permit',
          description: 'Standard work permit requiring Labour Market Impact Assessment from employer',
          category: 'work-permits',
          goalAchieved: 'Obtain work authorization in Canada through employer-sponsored LMIA',
          requirements: [
            {
              id: 'req_job_offer',
              name: 'Valid Job Offer',
              description: 'Must have a genuine job offer from a Canadian employer',
              type: 'eligibility',
              discoveryQuestion: 'Do you have a job offer from a Canadian employer?',
              satisfactionCriteria: ['Yes, I have a written job offer'],
              isFlexible: false,
              priority: 10
            },
            {
              id: 'req_lmia',
              name: 'Positive LMIA',
              description: 'Employer must obtain positive LMIA or be LMIA-exempt',
              type: 'document',
              discoveryQuestion: 'Has your employer applied for or received an LMIA?',
              satisfactionCriteria: ['Yes, positive LMIA received', 'LMIA application in progress'],
              isFlexible: false,
              priority: 9
            }
          ],
          blockers: [
            {
              id: 'block_inadmissible',
              name: 'Criminal Inadmissibility',
              description: 'Criminal record may prevent entry to Canada',
              discoveryQuestion: 'Do you have any criminal convictions or charges?',
              indicatorAnswers: ['Yes, I have a criminal record'],
              severity: 'hard',
              overcomingStrategies: ['Criminal Rehabilitation application', 'Temporary Resident Permit'],
              alternativePathways: []
            }
          ],
          unlockFactors: [
            {
              id: 'unlock_spouse',
              name: 'Spouse Work Rights',
              description: 'Spouse may be eligible for open work permit',
              discoveryQuestion: 'Are you married or in a common-law relationship?',
              unlockAnswers: ['Yes, I am married', 'Yes, common-law partner'],
              unlockedPathways: ['Spousal Open Work Permit'],
              priority: 5
            }
          ],
          alternatives: [
            {
              id: 'alt_study',
              name: 'Study Then Work',
              description: 'Come as student, then apply for PGWP after graduation',
              triggerConditions: ['No job offer available', 'Want to explore options'],
              steps: ['Apply to designated learning institution', 'Get study permit', 'Graduate', 'Apply for PGWP'],
              successLikelihood: 'high'
            }
          ],
          processingTime: '2-4 months',
          priorityScore: 10,
          isActive: true,
          source: 'expert',
          version: 1
        },
        {
          id: 'pathway_owp',
          name: 'open-work-permit',
          displayName: 'Open Work Permit (Spousal)',
          description: 'Work permit for spouses of skilled workers or students',
          category: 'work-permits',
          goalAchieved: 'Obtain unrestricted work authorization as spouse/partner',
          requirements: [
            {
              id: 'req_spouse_permit',
              name: 'Spouse on Valid Permit',
              description: 'Spouse must hold valid work permit (TEER 0-3) or study permit',
              type: 'eligibility',
              discoveryQuestion: 'Does your spouse have a valid work or study permit in Canada?',
              satisfactionCriteria: ['Yes, work permit', 'Yes, study permit'],
              isFlexible: false,
              priority: 10
            }
          ],
          blockers: [],
          unlockFactors: [],
          alternatives: [
            {
              id: 'alt_own_lmia',
              name: 'Get Own LMIA',
              description: 'Apply for own employer-specific work permit',
              triggerConditions: ['Spouse does not have valid permit'],
              steps: ['Find employer willing to do LMIA', 'Apply for LMIA work permit'],
              successLikelihood: 'medium'
            }
          ],
          processingTime: '1-3 months',
          priorityScore: 8,
          isActive: true,
          source: 'expert',
          version: 1
        },
        {
          id: 'pathway_pgwp',
          name: 'pgwp',
          displayName: 'Post-Graduation Work Permit',
          description: 'Work permit for graduates of Canadian institutions',
          category: 'work-permits',
          goalAchieved: 'Obtain work permit after graduating from Canadian school',
          requirements: [
            {
              id: 'req_graduate',
              name: 'Canadian Graduate',
              description: 'Must have graduated from eligible Canadian institution',
              type: 'eligibility',
              discoveryQuestion: 'Have you graduated from a designated learning institution (DLI) in Canada?',
              satisfactionCriteria: ['Yes, I graduated from a DLI'],
              isFlexible: false,
              priority: 10
            },
            {
              id: 'req_program_length',
              name: 'Program Length',
              description: 'Program must be at least 8 months long',
              type: 'eligibility',
              discoveryQuestion: 'How long was your study program?',
              satisfactionCriteria: ['8 months to 2 years', 'More than 2 years'],
              isFlexible: false,
              priority: 9
            }
          ],
          blockers: [
            {
              id: 'block_timing',
              name: 'Application Timing',
              description: 'Must apply within 180 days of getting final marks',
              discoveryQuestion: 'When did you receive your final marks/graduation letter?',
              indicatorAnswers: ['More than 180 days ago'],
              severity: 'hard',
              overcomingStrategies: [],
              alternativePathways: ['LMIA Work Permit']
            }
          ],
          unlockFactors: [],
          alternatives: [],
          processingTime: '2-3 months',
          priorityScore: 9,
          isActive: true,
          source: 'expert',
          version: 1
        }
      ],
      globalUnlockFactors: [
        {
          id: 'global_pr_eligible',
          name: 'PR Eligibility',
          description: 'Check if user may qualify for permanent residence directly',
          discoveryQuestion: 'Have you worked in Canada before or do you have Canadian education?',
          unlockAnswers: ['Yes, I have Canadian work experience', 'Yes, I have Canadian education'],
          unlockedPathways: ['Express Entry', 'PNP'],
          priority: 8
        }
      ],
      universalDiscoveryQuestions: [
        {
          question: 'What is your highest level of education?',
          purpose: 'Determine eligibility for various programs',
          priority: 10
        },
        {
          question: 'Do you have any family members who are Canadian citizens or permanent residents?',
          purpose: 'Unlock family sponsorship pathways',
          priority: 9
        }
      ]
    };
    
    setFormData(exampleData);
    setExpandedPathways({ 'pathway_lmia': true });
    setShowModal(true);
    showToast('Example Work Permits map loaded - review and save!', 'info');
  };

  // Open modal for editing
  const handleEditMap = (map) => {
    setEditingMap(map);
    setFormData({
      domain: map.domain || '',
      domainDisplayName: map.domainDisplayName || '',
      status: map.status || 'draft',
      pathways: map.pathways || [],
      globalUnlockFactors: map.globalUnlockFactors || [],
      universalDiscoveryQuestions: map.universalDiscoveryQuestions || []
    });
    setExpandedPathways({});
    setShowModal(true);
  };

  // Save map
  const handleSaveMap = async () => {
    if (!formData.domain || !formData.domainDisplayName) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      setSaving(true);
      
      if (editingMap) {
        await axios.put(`/api/brain/v2/opportunity-maps/${editingMap._id}`, formData);
        showToast('Opportunity map updated successfully', 'success');
      } else {
        await axios.post('/api/brain/v2/opportunity-maps', formData);
        showToast('Opportunity map created successfully', 'success');
      }
      
      setShowModal(false);
      fetchMaps();
    } catch (error) {
      console.error('Error saving map:', error);
      showToast('Failed to save opportunity map', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete map
  const handleDeleteMap = async (mapId) => {
    const confirmed = await confirm({
      title: 'Delete Opportunity Map',
      message: 'Are you sure you want to delete this opportunity map? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await axios.delete(`/api/brain/v2/opportunity-maps/${mapId}`);
      showToast('Opportunity map deleted', 'success');
      fetchMaps();
    } catch (error) {
      console.error('Error deleting map:', error);
      showToast('Failed to delete opportunity map', 'error');
    }
  };

  // Add a new pathway
  const handleAddPathway = () => {
    const newPathway = {
      id: `pathway_${Date.now()}`,
      name: '',
      displayName: 'New Pathway',
      description: '',
      category: formData.domain,
      goalAchieved: '',
      requirements: [],
      blockers: [],
      unlockFactors: [],
      alternatives: [],
      priorityScore: 0,
      isActive: true,
      source: 'expert',
      version: 1
    };
    
    setFormData(prev => ({
      ...prev,
      pathways: [...prev.pathways, newPathway]
    }));
    
    setExpandedPathways(prev => ({
      ...prev,
      [newPathway.id]: true
    }));
  };

  // Update pathway
  const updatePathway = (pathwayId, field, value) => {
    setFormData(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => 
        p.id === pathwayId ? { ...p, [field]: value } : p
      )
    }));
  };

  // Delete pathway
  const deletePathway = (pathwayId) => {
    setFormData(prev => ({
      ...prev,
      pathways: prev.pathways.filter(p => p.id !== pathwayId)
    }));
  };

  // Start editing an item (new or existing)
  const startEditingItem = (pathwayId, itemType, itemId = null, existingData = null) => {
    if (existingData) {
      setEditingItemData({
        name: existingData.name || '',
        description: existingData.description || '',
        discoveryQuestion: existingData.discoveryQuestion || ''
      });
    } else {
      setEditingItemData({ name: '', description: '', discoveryQuestion: '' });
    }
    setEditingItem({ pathwayId, itemType, itemId, isNew: !itemId });
  };

  // Cancel editing
  const cancelEditingItem = () => {
    setEditingItem(null);
    setEditingItemData({ name: '', description: '', discoveryQuestion: '' });
  };

  // Save edited item
  const saveEditingItem = () => {
    if (!editingItem || !editingItemData.name.trim()) {
      showToast('Please enter a name for this item', 'warning');
      return;
    }

    const { pathwayId, itemType, itemId, isNew } = editingItem;
    const { name, description, discoveryQuestion } = editingItemData;

    setFormData(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => {
        if (p.id !== pathwayId) return p;

        const arrayKey = itemType === 'requirement' ? 'requirements' 
          : itemType === 'blocker' ? 'blockers'
          : itemType === 'unlockFactor' ? 'unlockFactors'
          : 'alternatives';

        if (isNew) {
          // Add new item
          const newItem = {
            id: `${itemType}_${Date.now()}`,
            name: name.trim(),
            description: description.trim(),
            discoveryQuestion: discoveryQuestion.trim(),
            ...(itemType === 'requirement' && { type: 'eligibility', satisfactionCriteria: [], isFlexible: false, priority: 0 }),
            ...(itemType === 'blocker' && { type: 'hard', severity: 'blocking', resolutionPath: '' }),
            ...(itemType === 'unlockFactor' && { question: discoveryQuestion.trim(), opensPathways: [], priority: 0 }),
            ...(itemType === 'alternative' && { conditionsForRecommendation: [], recommendationPriority: 0 })
          };
          return { ...p, [arrayKey]: [...p[arrayKey], newItem] };
        } else {
          // Update existing item
          return {
            ...p,
            [arrayKey]: p[arrayKey].map(item => 
              item.id === itemId 
                ? { ...item, name: name.trim(), description: description.trim(), discoveryQuestion: discoveryQuestion.trim() }
                : item
            )
          };
        }
      })
    }));

    cancelEditingItem();
  };

  // Remove an item from a pathway
  const removeItem = (pathwayId, itemType, itemId) => {
    const arrayKey = itemType === 'requirement' ? 'requirements' 
      : itemType === 'blocker' ? 'blockers'
      : itemType === 'unlockFactor' ? 'unlockFactors'
      : 'alternatives';

    setFormData(prev => ({
      ...prev,
      pathways: prev.pathways.map(p => 
        p.id === pathwayId 
          ? { ...p, [arrayKey]: p[arrayKey].filter(item => item.id !== itemId) }
          : p
      )
    }));
  };

  // Add requirement to pathway (triggers inline edit)
  const addRequirement = (pathwayId) => {
    startEditingItem(pathwayId, 'requirement');
  };

  // Add blocker to pathway (triggers inline edit)
  const addBlocker = (pathwayId) => {
    startEditingItem(pathwayId, 'blocker');
  };

  // Add unlock factor to pathway (triggers inline edit)
  const addUnlockFactor = (pathwayId) => {
    startEditingItem(pathwayId, 'unlockFactor');
  };

  // Add alternative strategy to pathway (triggers inline edit)
  const addAlternative = (pathwayId) => {
    startEditingItem(pathwayId, 'alternative');
  };

  // Toggle pathway expansion
  const togglePathway = (pathwayId) => {
    setExpandedPathways(prev => ({
      ...prev,
      [pathwayId]: !prev[pathwayId]
    }));
  };

  // Activate/Deactivate map
  const handleToggleStatus = async (map) => {
    const newStatus = map.status === 'active' ? 'draft' : 'active';
    try {
      await axios.put(`/api/brain/v2/opportunity-maps/${map._id}`, { status: newStatus });
      showToast(`Map ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
      fetchMaps();
    } catch (error) {
      showToast('Failed to update status', 'error');
    }
  };

  if (loading) {
    return <ImavenLoadingScreen />;
  }

  return (
    <ModuleContainer>
      <Header>
        <TitleSection>
          <Title>
            {Icons.map}
            Opportunity Maps
          </Title>
          <Subtitle>
            Define pathways with requirements, blockers, unlock factors, and alternatives for intelligent opportunity discovery
          </Subtitle>
        </TitleSection>
        <ActionButtons>
          <Button $secondary onClick={fetchMaps}>
            {Icons.refresh} Refresh
          </Button>
          <Button $success onClick={handleSeedDefaults} disabled={seeding}>
            {seeding ? Icons.loader : Icons.map} {seeding ? 'Importing...' : 'Import All 21 Templates'}
          </Button>
          <Button $primary onClick={handleCreateMap}>
            {Icons.plus} Create Map
          </Button>
        </ActionButtons>
      </Header>

      {/* Filter Bar */}
      {maps.length > 0 && (
        <FilterBar>
          <FilterButton 
            $active={statusFilter === 'all'} 
            onClick={() => setStatusFilter('all')}
          >
            All
            <FilterCount $active={statusFilter === 'all'}>{maps.length}</FilterCount>
          </FilterButton>
          <FilterButton 
            $active={statusFilter === 'active'} 
            onClick={() => setStatusFilter('active')}
          >
            {Icons.check} Active
            <FilterCount $active={statusFilter === 'active'}>
              {maps.filter(m => m.status === 'active').length}
            </FilterCount>
          </FilterButton>
          <FilterButton 
            $active={statusFilter === 'draft'} 
            onClick={() => setStatusFilter('draft')}
          >
            Draft
            <FilterCount $active={statusFilter === 'draft'}>
              {maps.filter(m => m.status === 'draft').length}
            </FilterCount>
          </FilterButton>
          <FilterButton 
            $active={statusFilter === 'archived'} 
            onClick={() => setStatusFilter('archived')}
          >
            Archived
            <FilterCount $active={statusFilter === 'archived'}>
              {maps.filter(m => m.status === 'archived').length}
            </FilterCount>
          </FilterButton>
        </FilterBar>
      )}

      {maps.length === 0 ? (
        <EmptyState>
          {Icons.map}
          <h3>No Opportunity Maps Yet</h3>
          <p>
            Create your first Opportunity Map to help your AI discover hidden opportunities 
            and suggest alternative paths when the primary path fails.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button $success onClick={handleSeedDefaults} disabled={seeding} style={{ fontSize: '14px' }}>
              {seeding ? Icons.loader : Icons.map} {seeding ? 'Importing...' : 'Import All Immigration Templates (Recommended)'}
            </Button>
          </div>
          <p style={{ marginTop: '16px', color: 'rgba(248, 250, 252, 0.5)', fontSize: '13px' }}>
            Creates 21 comprehensive opportunity maps covering all Canadian immigration pathways:
            Visitor Entry, Study Permits, Work Permits, Status Transitions, Express Entry, PNPs (all provinces), 
            Quebec Immigration, Business Immigration, Family Sponsorship, H&C, Refugee Protection, 
            Citizenship, Compliance, Restoration, Refusal Recovery, Inadmissibility, and Meta Strategy maps. 
            You can customize them after import.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
            <Button $secondary onClick={handleCreateMap}>
              {Icons.plus} Create Custom Map
            </Button>
          </div>
        </EmptyState>
      ) : (
        <MapsGrid>
          {maps
            .filter(map => statusFilter === 'all' || map.status === statusFilter)
            .map(map => (
            <MapCard key={map._id} onClick={() => handleEditMap(map)}>
              <MapHeader>
                <MapTitle>{map.domainDisplayName}</MapTitle>
                <MapStatus $status={map.status}>{map.status}</MapStatus>
              </MapHeader>
              <MapStats>
                <MapStat>
                  {Icons.pathway}
                  {map.pathways?.length || 0} Pathways
                </MapStat>
                <MapStat>
                  {Icons.unlock}
                  {map.globalUnlockFactors?.length || 0} Unlock Factors
                </MapStat>
              </MapStats>
              <MapActions onClick={e => e.stopPropagation()}>
                <IconButton onClick={() => handleToggleStatus(map)} title={map.status === 'active' ? 'Deactivate' : 'Activate'}>
                  {map.status === 'active' ? Icons.block : Icons.check}
                </IconButton>
                <IconButton onClick={() => handleEditMap(map)} title="Edit">
                  {Icons.edit}
                </IconButton>
                <IconButton $danger onClick={() => handleDeleteMap(map._id)} title="Delete">
                  {Icons.trash}
                </IconButton>
              </MapActions>
            </MapCard>
          ))}
          
          {/* Empty state when filter has no results */}
          {maps.length > 0 && maps.filter(m => statusFilter === 'all' || m.status === statusFilter).length === 0 && (
            <div style={{ 
              gridColumn: '1 / -1', 
              textAlign: 'center', 
              padding: '40px', 
              color: 'rgba(248, 250, 252, 0.5)' 
            }}>
              No {statusFilter} maps found. Try a different filter.
            </div>
          )}
        </MapsGrid>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingMap ? 'Edit Opportunity Map' : 'Create Opportunity Map'}
              </ModalTitle>
              <IconButton onClick={() => setShowModal(false)}>
                {Icons.x}
              </IconButton>
            </ModalHeader>
            
            <ModalBody>
              <FormGroup>
                <Label>Domain/Category Name *</Label>
                <Input
                  placeholder="e.g., work-permits"
                  value={formData.domain}
                  onChange={e => setFormData(prev => ({ ...prev, domain: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Display Name *</Label>
                <Input
                  placeholder="e.g., Work Permit Pathways"
                  value={formData.domainDisplayName}
                  onChange={e => setFormData(prev => ({ ...prev, domainDisplayName: e.target.value }))}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </Select>
              </FormGroup>

              {/* Universal screening questions — asked of everyone who talks to this map, before pathway questions.
                  Until now these were only editable in the database; Test Bench findings point here. */}
              <FormGroup>
                <Label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Universal screening questions ({(formData.universalDiscoveryQuestions || []).length})</span>
                  <Button $secondary style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => setFormData(prev => ({ ...prev, universalDiscoveryQuestions: [...(prev.universalDiscoveryQuestions || []), { question: '', purpose: '', priority: 10 }] }))}>
                    {Icons.plus} Add question
                  </Button>
                </Label>
                <div style={{ fontSize: '12px', color: '#8b98a5', marginBottom: '8px' }}>
                  Asked of every visitor regardless of pathway. A question that belongs to one job or employer should be a requirement on that pathway instead.
                </div>
                {(formData.universalDiscoveryQuestions || []).map((q, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                    <Input
                      value={q.question || ''}
                      placeholder="Question asked of everyone"
                      onChange={e => setFormData(prev => ({ ...prev, universalDiscoveryQuestions: (prev.universalDiscoveryQuestions || []).map((x, j) => j === i ? { ...x, question: e.target.value } : x) }))}
                    />
                    <ItemRemoveBtn title="Remove" onClick={() => setFormData(prev => ({ ...prev, universalDiscoveryQuestions: (prev.universalDiscoveryQuestions || []).filter((_, j) => j !== i) }))}>
                      {Icons.x}
                    </ItemRemoveBtn>
                  </div>
                ))}
              </FormGroup>

              {/* Pathways Section */}
              <FormGroup>
                <Label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Pathways ({formData.pathways.length})</span>
                  <Button $secondary style={{ padding: '6px 12px', fontSize: '12px' }} onClick={handleAddPathway}>
                    {Icons.plus} Add Pathway
                  </Button>
                </Label>
                
                {formData.pathways.map(pathway => (
                  <PathwaySection key={pathway.id}>
                    <PathwayHeader onClick={() => togglePathway(pathway.id)}>
                      <PathwayTitle>
                        {Icons.pathway}
                        {pathway.displayName || 'Unnamed Pathway'}
                      </PathwayTitle>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconButton $danger onClick={(e) => { e.stopPropagation(); deletePathway(pathway.id); }}>
                          {Icons.trash}
                        </IconButton>
                        {expandedPathways[pathway.id] ? Icons.chevronUp : Icons.chevronDown}
                      </div>
                    </PathwayHeader>
                    
                    {expandedPathways[pathway.id] && (
                      <PathwayBody>
                        <FormGroup>
                          <Label>Pathway Name</Label>
                          <Input
                            placeholder="e.g., LMIA Work Permit"
                            value={pathway.displayName}
                            onChange={e => updatePathway(pathway.id, 'displayName', e.target.value)}
                          />
                        </FormGroup>
                        
                        <FormGroup>
                          <Label>Description</Label>
                          <TextArea
                            placeholder="Describe this pathway..."
                            value={pathway.description}
                            onChange={e => updatePathway(pathway.id, 'description', e.target.value)}
                          />
                        </FormGroup>
                        
                        <FormGroup>
                          <Label>Goal Achieved</Label>
                          <Input
                            placeholder="e.g., Obtain work authorization in Canada"
                            value={pathway.goalAchieved}
                            onChange={e => updatePathway(pathway.id, 'goalAchieved', e.target.value)}
                          />
                        </FormGroup>

                        {/* Eligibility Criteria */}
                        <FormGroup>
                          <Label>Eligibility Criteria <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400, fontSize: 12 }}>(optional - helps AI recommend this pathway)</span></Label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <div>
                              <Label style={{ fontSize: 11, marginBottom: 4 }}>Age Range</Label>
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <Input
                                  type="number"
                                  placeholder="Min"
                                  style={{ width: 70 }}
                                  value={pathway.eligibilityCriteria?.ageRange?.min || ''}
                                  onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                    ...pathway.eligibilityCriteria,
                                    ageRange: { ...pathway.eligibilityCriteria?.ageRange, min: e.target.value ? parseInt(e.target.value) : undefined }
                                  })}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.4)' }}>-</span>
                                <Input
                                  type="number"
                                  placeholder="Max"
                                  style={{ width: 70 }}
                                  value={pathway.eligibilityCriteria?.ageRange?.max || ''}
                                  onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                    ...pathway.eligibilityCriteria,
                                    ageRange: { ...pathway.eligibilityCriteria?.ageRange, max: e.target.value ? parseInt(e.target.value) : undefined }
                                  })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label style={{ fontSize: 11, marginBottom: 4 }}>Education Level</Label>
                              <Input
                                placeholder="e.g., post-secondary"
                                value={pathway.eligibilityCriteria?.educationLevel || ''}
                                onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                  ...pathway.eligibilityCriteria,
                                  educationLevel: e.target.value || undefined
                                })}
                              />
                            </div>
                            <div>
                              <Label style={{ fontSize: 11, marginBottom: 4 }}>Language Score</Label>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <Input
                                  placeholder="Test (CLB/IELTS)"
                                  style={{ flex: 1 }}
                                  value={pathway.eligibilityCriteria?.languageScore?.test || ''}
                                  onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                    ...pathway.eligibilityCriteria,
                                    languageScore: { ...pathway.eligibilityCriteria?.languageScore, test: e.target.value || undefined }
                                  })}
                                />
                                <Input
                                  type="number"
                                  placeholder="Min"
                                  style={{ width: 60 }}
                                  value={pathway.eligibilityCriteria?.languageScore?.min || ''}
                                  onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                    ...pathway.eligibilityCriteria,
                                    languageScore: { ...pathway.eligibilityCriteria?.languageScore, min: e.target.value ? parseFloat(e.target.value) : undefined }
                                  })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label style={{ fontSize: 11, marginBottom: 4 }}>Work Experience</Label>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <Input
                                  type="number"
                                  placeholder="Min years"
                                  style={{ width: 80 }}
                                  value={pathway.eligibilityCriteria?.workExperience?.minYears || ''}
                                  onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                    ...pathway.eligibilityCriteria,
                                    workExperience: { ...pathway.eligibilityCriteria?.workExperience, minYears: e.target.value ? parseInt(e.target.value) : undefined }
                                  })}
                                />
                                <Input
                                  placeholder="Type (e.g., skilled)"
                                  style={{ flex: 1 }}
                                  value={pathway.eligibilityCriteria?.workExperience?.type || ''}
                                  onChange={e => updatePathway(pathway.id, 'eligibilityCriteria', {
                                    ...pathway.eligibilityCriteria,
                                    workExperience: { ...pathway.eligibilityCriteria?.workExperience, type: e.target.value || undefined }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                        </FormGroup>

                        {/* Requirements */}
                        <SubSection>
                          <SubSectionTitle $color="#10b981">
                            {Icons.check} Requirements ({pathway.requirements.length})
                          </SubSectionTitle>
                          <ItemList>
                            {pathway.requirements.map(req => (
                              <ItemTag 
                                key={req.id} 
                                $bg="rgba(16, 185, 129, 0.15)" 
                                $border="rgba(16, 185, 129, 0.3)" 
                                $color="#6ee7b7"
                                onClick={() => startEditingItem(pathway.id, 'requirement', req.id, req)}
                                title="Click to edit"
                              >
                                {req.name || 'Unnamed requirement'}
                                <ItemRemoveBtn onClick={(e) => { e.stopPropagation(); removeItem(pathway.id, 'requirement', req.id); }} title="Remove">
                                  {Icons.x}
                                </ItemRemoveBtn>
                              </ItemTag>
                            ))}
                            {editingItem?.pathwayId === pathway.id && editingItem?.itemType === 'requirement' ? (
                              <InlineEditWrapper>
                                <InlineInput
                                  placeholder="e.g., Has job offer from Canadian employer"
                                  value={editingItemData.name}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, name: e.target.value }))}
                                  autoFocus
                                />
                                <InlineTextArea
                                  placeholder="Description (optional): What exactly is required?"
                                  value={editingItemData.description}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, description: e.target.value }))}
                                />
                                <InlineEditRow>
                                  <InlineButton onClick={cancelEditingItem}>Cancel</InlineButton>
                                  <InlineButton $primary onClick={saveEditingItem}>Save</InlineButton>
                                </InlineEditRow>
                              </InlineEditWrapper>
                            ) : (
                              <AddItemButton onClick={() => addRequirement(pathway.id)}>
                                {Icons.plus} Add Requirement
                              </AddItemButton>
                            )}
                          </ItemList>
                        </SubSection>

                        {/* Blockers */}
                        <SubSection>
                          <SubSectionTitle $color="#ef4444">
                            {Icons.block} Blockers ({pathway.blockers.length})
                          </SubSectionTitle>
                          <ItemList>
                            {pathway.blockers.map(blocker => (
                              <ItemTag 
                                key={blocker.id} 
                                $bg="rgba(239, 68, 68, 0.15)" 
                                $border="rgba(239, 68, 68, 0.3)" 
                                $color="#fca5a5"
                                onClick={() => startEditingItem(pathway.id, 'blocker', blocker.id, blocker)}
                                title="Click to edit"
                              >
                                {blocker.name || 'Unnamed blocker'}
                                <ItemRemoveBtn onClick={(e) => { e.stopPropagation(); removeItem(pathway.id, 'blocker', blocker.id); }} title="Remove">
                                  {Icons.x}
                                </ItemRemoveBtn>
                              </ItemTag>
                            ))}
                            {editingItem?.pathwayId === pathway.id && editingItem?.itemType === 'blocker' ? (
                              <InlineEditWrapper>
                                <InlineInput
                                  placeholder="e.g., Criminal inadmissibility"
                                  value={editingItemData.name}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, name: e.target.value }))}
                                  autoFocus
                                />
                                <InlineTextArea
                                  placeholder="Description (optional): What blocks this pathway?"
                                  value={editingItemData.description}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, description: e.target.value }))}
                                />
                                <InlineEditRow>
                                  <InlineButton onClick={cancelEditingItem}>Cancel</InlineButton>
                                  <InlineButton $primary onClick={saveEditingItem}>Save</InlineButton>
                                </InlineEditRow>
                              </InlineEditWrapper>
                            ) : (
                              <AddItemButton onClick={() => addBlocker(pathway.id)}>
                                {Icons.plus} Add Blocker
                              </AddItemButton>
                            )}
                          </ItemList>
                        </SubSection>

                        {/* Unlock Factors */}
                        <SubSection>
                          <SubSectionTitle $color="#f59e0b">
                            {Icons.unlock} Unlock Factors ({pathway.unlockFactors.length})
                          </SubSectionTitle>
                          <ItemList>
                            {pathway.unlockFactors.map(factor => (
                              <ItemTag 
                                key={factor.id} 
                                $bg="rgba(245, 158, 11, 0.15)" 
                                $border="rgba(245, 158, 11, 0.3)" 
                                $color="#fcd34d"
                                onClick={() => startEditingItem(pathway.id, 'unlockFactor', factor.id, factor)}
                                title="Click to edit"
                              >
                                {factor.name || 'Unnamed factor'}
                                <ItemRemoveBtn onClick={(e) => { e.stopPropagation(); removeItem(pathway.id, 'unlockFactor', factor.id); }} title="Remove">
                                  {Icons.x}
                                </ItemRemoveBtn>
                              </ItemTag>
                            ))}
                            {editingItem?.pathwayId === pathway.id && editingItem?.itemType === 'unlockFactor' ? (
                              <InlineEditWrapper>
                                <InlineInput
                                  placeholder="e.g., Has Canadian spouse"
                                  value={editingItemData.name}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, name: e.target.value }))}
                                  autoFocus
                                />
                                <InlineTextArea
                                  placeholder="Discovery question: What to ask to discover this?"
                                  value={editingItemData.discoveryQuestion}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, discoveryQuestion: e.target.value }))}
                                />
                                <InlineEditRow>
                                  <InlineButton onClick={cancelEditingItem}>Cancel</InlineButton>
                                  <InlineButton $primary onClick={saveEditingItem}>Save</InlineButton>
                                </InlineEditRow>
                              </InlineEditWrapper>
                            ) : (
                              <AddItemButton onClick={() => addUnlockFactor(pathway.id)}>
                                {Icons.plus} Add Unlock Factor
                              </AddItemButton>
                            )}
                          </ItemList>
                        </SubSection>

                        {/* Alternatives */}
                        <SubSection>
                          <SubSectionTitle $color="#6366f1">
                            {Icons.alt} Alternatives ({pathway.alternatives.length})
                          </SubSectionTitle>
                          <ItemList>
                            {pathway.alternatives.map(alt => (
                              <ItemTag 
                                key={alt.id}
                                $bg="rgba(99, 102, 241, 0.15)" 
                                $border="rgba(99, 102, 241, 0.3)" 
                                $color="#a5b4fc"
                                onClick={() => startEditingItem(pathway.id, 'alternative', alt.id, alt)}
                                title="Click to edit"
                              >
                                {alt.name || 'Unnamed alternative'}
                                <ItemRemoveBtn onClick={(e) => { e.stopPropagation(); removeItem(pathway.id, 'alternative', alt.id); }} title="Remove">
                                  {Icons.x}
                                </ItemRemoveBtn>
                              </ItemTag>
                            ))}
                            {editingItem?.pathwayId === pathway.id && editingItem?.itemType === 'alternative' ? (
                              <InlineEditWrapper>
                                <InlineInput
                                  placeholder="e.g., Study Permit instead"
                                  value={editingItemData.name}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, name: e.target.value }))}
                                  autoFocus
                                />
                                <InlineTextArea
                                  placeholder="Description: When to suggest this alternative?"
                                  value={editingItemData.description}
                                  onChange={e => setEditingItemData(prev => ({ ...prev, description: e.target.value }))}
                                />
                                <InlineEditRow>
                                  <InlineButton onClick={cancelEditingItem}>Cancel</InlineButton>
                                  <InlineButton $primary onClick={saveEditingItem}>Save</InlineButton>
                                </InlineEditRow>
                              </InlineEditWrapper>
                            ) : (
                              <AddItemButton onClick={() => addAlternative(pathway.id)}>
                                {Icons.plus} Add Alternative
                              </AddItemButton>
                            )}
                          </ItemList>
                        </SubSection>
                      </PathwayBody>
                    )}
                  </PathwaySection>
                ))}
              </FormGroup>
            </ModalBody>
            
            <ModalFooter>
              <Button $secondary onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button $primary onClick={handleSaveMap} disabled={saving}>
                {saving ? (
                  <>
                    <LoadingSpinner>{Icons.loader}</LoadingSpinner>
                    Saving...
                  </>
                ) : (
                  <>
                    {Icons.save}
                    {editingMap ? 'Update Map' : 'Create Map'}
                  </>
                )}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </ModuleContainer>
  );
};

export default OpportunityMapsModule;

